/**
 * Reading a schedule as a person reads it: which day is today, and what is
 * happening right now.
 *
 * Pure functions, deliberately: everything here is a date calculation with a
 * timezone in it, which is exactly the kind of thing that looks right in a
 * screenshot and is wrong twice a year. `schedule.test.ts` pins the DST case,
 * the midnight case and the "no duration" case, none of which are reachable by
 * looking at the page on the afternoon you wrote it.
 *
 * ⚠️ MOSCOW IS THE AUTHORITATIVE ZONE, here as everywhere else (DECISION-12).
 * The day an occurrence belongs to is its MOSCOW day, not the viewer's — a
 * member in Warsaw looking at a 00:30 МСК raid must see it grouped under the
 * same heading the member in Moscow sees, or the two of them are reading
 * different schedules while quoting the same page at each other.
 */

const MOSCOW = 'Europe/Moscow';

/**
 * The two zones every time on the site is printed in (DECISION-27).
 *
 * ⚠️ DUPLICATES `DISPLAY_TIMEZONES` in @vlastiq/db, and has to: that package
 * reaches for bun:sqlite, which cannot be bundled for a browser. Keep the two
 * lists in step — the bot posts one and the site shows the other, and a member
 * comparing them is comparing the same activity. Minsk was dropped from both;
 * it is UTC+3 year-round and always equalled Moscow.
 *
 * ⚠️ `short` IS WHAT MAKES THE TABLE FIT A PHONE, and it is not decoration.
 * «Активность | Москва | Варшава» measures 319px against a 318px content box on
 * a 390px screen and 71px over on a 320px one — the two headings, not the
 * times, are what set those columns' width. The abbreviations are the ones the
 * BOT already posts, so nobody is learning a second vocabulary.
 */
export const ZONES = [
  { id: 'Europe/Moscow', label: 'Москва', short: 'МСК' },
  { id: 'Europe/Warsaw', label: 'Варшава', short: 'ВРШ' },
] as const;

/** The fields these functions need. Anything wider is passed through intact. */
export interface ScheduleOccurrence {
  at: string;
  durationMinutes: number | null;
}

/**
 * How long an event with NO recorded duration is assumed to run.
 *
 * `durationMinutes` is nullable and most events do not set it — the same fact
 * that made `LOOT_ANNOUNCEMENT_LOOKBACK_HOURS` a constant on the server. A
 * highlight that only ever fired for events carrying a duration would be
 * invisible in this deployment, which is indistinguishable from broken.
 */
export const ASSUMED_DURATION_MINUTES = 60;

/**
 * «2026-08-12» for the Moscow day an instant falls in.
 *
 * Built from `formatToParts` rather than by formatting to a locale that happens
 * to print ISO order. `en-CA` does today; it is not a documented guarantee, and
 * the failure mode — every occurrence grouped under one wrong key — is silent.
 */
export function moscowDay(iso: string | number | Date): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: MOSCOW,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date(iso));

  const pick = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? '';

  return `${pick('year')}-${pick('month')}-${pick('day')}`;
}

/** «среда, 12 августа» — Intl gives it lowercase; the capital is a CSS rule. */
export function dayLabel(iso: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: MOSCOW,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(new Date(iso));
}

/** «21:00» in the given zone. */
export function timeIn(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

/**
 * Whether an occurrence is happening at `now`.
 *
 * ⚠️ MORE THAN ONE ROW CAN BE ONGOING, AND TRYING TO PREVENT THAT WAS WRONG.
 *
 * The first version ended a duration-less event at the START of the next
 * occurrence, on the theory that «что идёт сейчас» should have exactly one
 * answer. Rendered against a real schedule it did the opposite: a clan evening
 * with no recorded duration lost its highlight twelve minutes in, because a
 * fifteen-minute respawn timer happened to be next. This deployment's schedule
 * is mostly frequent game timers, so the long clan event — the one people are
 * actually sitting in when they open this page — is precisely the one that
 * would never stay marked.
 *
 * Both errors are guesses; they are not equally bad. Two rows marked within an
 * hour of each other is loose but true of the evening. Dropping the mark off
 * the event in progress reads as the feature being broken.
 */
export function isOngoing(occurrence: ScheduleOccurrence, now: number): boolean {
  const start = new Date(occurrence.at).getTime();
  if (Number.isNaN(start) || now < start) return false;

  // `> 0` rather than `!= null`: a stored 0 is not a duration, and treating it
  // as one would end the event at the instant it began.
  const minutes =
    occurrence.durationMinutes !== null && occurrence.durationMinutes > 0
      ? occurrence.durationMinutes
      : ASSUMED_DURATION_MINUTES;

  return now < start + minutes * 60_000;
}

/**
 * Whether an occurrence has already finished.
 *
 * ⚠️ THIS EXISTS BECAUSE THE API STOPPED HIDING THE PAST. `/api/schedule` now
 * opens its window at Moscow midnight rather than at `now` — it had to, or an
 * event in progress was absent from the very response that is supposed to mark
 * it as running — and the cost of that is that today's card also carries
 * everything already over. Drawn identically to an upcoming event, a finished
 * 10:00 raid is worse than the omission was.
 */
export function isPast(occurrence: ScheduleOccurrence, now: number): boolean {
  const start = new Date(occurrence.at).getTime();
  if (Number.isNaN(start)) return false;
  return now >= start && !isOngoing(occurrence, now);
}

export interface ScheduleItem<T extends ScheduleOccurrence> {
  occurrence: T;
  isOngoing: boolean;
  isPast: boolean;
}

export interface ScheduleDay<T extends ScheduleOccurrence> {
  /** The Moscow day, «2026-08-12». Unique — unlike the rendered label. */
  key: string;
  label: string;
  isToday: boolean;
  items: ScheduleItem<T>[];
}

/**
 * Group occurrences into Moscow days, marking today and whatever is running.
 *
 * ⚠️ THE MAP IS KEYED ON THE ISO DAY, NOT ON THE RENDERED LABEL. «12 августа»
 * carries no year, so a range long enough to wrap — and the admin page can ask
 * for one — would fold two days a year apart into a single card. Insertion
 * order still decides the order of the cards, which is why the input is sorted
 * first rather than trusted to arrive that way.
 */
export function groupByDay<T extends ScheduleOccurrence>(
  occurrences: readonly T[],
  now: number,
): ScheduleDay<T>[] {
  const sorted = [...occurrences].sort(
    (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime(),
  );
  const today = moscowDay(now);

  const days = new Map<string, ScheduleDay<T>>();
  for (const occurrence of sorted) {
    const key = moscowDay(occurrence.at);
    let day = days.get(key);
    if (!day) {
      day = { key, label: dayLabel(occurrence.at), isToday: key === today, items: [] };
      days.set(key, day);
    }

    day.items.push({
      occurrence,
      isOngoing: isOngoing(occurrence, now),
      isPast: isPast(occurrence, now),
    });
  }

  return [...days.values()];
}
